import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Monitor, Smartphone } from 'lucide-react';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { Badge, Button, ErrorNotice, PageHeader, Panel, Spinner, formatRelative, useToast } from '../../ui';
import { PasswordForm } from './PasswordForm';

interface MySession {
  id: string;
  createdAt: string;
  lastSeenAt: string;
  ip: string | null;
  userAgent: string | null;
  current: boolean;
}

function describeAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'device';
  return `${browser} on ${os}`;
}

export function AccountPage() {
  const { me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const sessions = useQuery({ queryKey: ['my-sessions'], queryFn: () => api.get<MySession[]>('/api/auth/sessions') });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/auth/sessions/${id}`),
    onSuccess: () => {
      toast('Signed out of that device');
      void qc.invalidateQueries({ queryKey: ['my-sessions'] });
    },
  });

  useEffect(() => {
    if (location.hash === '#password') document.getElementById('password')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const branchNames = me.branches.map((b) => b.name).join(', ') || 'No active branch';

  return (
    <>
      <PageHeader title="My account" description="Your details, password and the devices you're signed in on." />
      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <Panel title="Profile">
          <dl className="divide-y divide-line">
            {[
              ['Name', me.user.fullName],
              ['Username', me.user.username],
              ['Role', me.user.roleName],
              ['Branches', branchNames],
              ['Email', me.user.email ?? '—'],
            ].map(([k, v]) => (
              <div key={k} className="grid grid-cols-[120px_1fr] gap-3 px-5 py-3">
                <dt className="text-muted">{k}</dt>
                <dd className="min-w-0 break-words">{v}</dd>
              </div>
            ))}
          </dl>
          <p className="border-t border-line px-5 py-3 text-sm text-muted">To change your name, role or branches, ask someone who manages staff.</p>
        </Panel>

        <Panel title="Change password" className="scroll-mt-24">
          <div id="password" className="p-5">
            <PasswordForm />
          </div>
        </Panel>

        <Panel title="Where you're signed in" className="lg:col-span-2">
          {sessions.isLoading && <Spinner />}
          {sessions.error && (
            <div className="p-5">
              <ErrorNotice error={sessions.error} onRetry={() => sessions.refetch()} />
            </div>
          )}
          {sessions.data && (
            <ul className="divide-y divide-line">
              {sessions.data.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-4 px-5 py-3.5">
                  <span className="grid size-9 place-items-center rounded-full bg-paper text-muted">
                    {/Android|iPhone|iPad/.test(s.userAgent ?? '') ? <Smartphone className="size-4" /> : <Monitor className="size-4" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {describeAgent(s.userAgent)} {s.current && <Badge tone="ok">This device</Badge>}
                    </p>
                    <p className="text-sm text-muted">
                      {s.ip ?? 'Unknown address'} — last active {formatRelative(s.lastSeenAt).toLowerCase()}
                    </p>
                  </div>
                  {!s.current && (
                    <Button size="sm" onClick={() => revoke.mutate(s.id)} busy={revoke.isPending && revoke.variables === s.id}>
                      Sign out
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
